import React, { useCallback, useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, MapPin, Plus, Trash2, Upload, Video, X } from "lucide-react";
import { toast } from "sonner";
import { createProject, deleteProject, fileUrl, listProjects, updateProject } from "../lib/api";

const CATEGORIES = ["Residential", "Commercial", "Interiors", "PEB", "Structural", "Drawings"];
const STATUSES = [
  { key: "completed", label: "Completed", color: "var(--m-success)" },
  { key: "ongoing", label: "Ongoing", color: "var(--m-warning)" },
  { key: "upcoming", label: "Upcoming", color: "var(--m-teal)" },
];
const STATUS_META = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

export const ProjectManager = ({ onCountChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProjects();
      setItems(data);
      onCountChange?.(data.length);
    } catch (err) {
      console.error("Failed to load projects:", err);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await deleteProject(id);
      setItems((arr) => arr.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Delete failed");
    }
  };

  const onStatusChange = async (id, status) => {
    try {
      const updated = await updateProject(id, { status });
      setItems((arr) => arr.map((p) => (p.id === id ? updated : p)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-6" data-testid="project-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--m-ink-2)]">
          {items.length} project{items.length !== 1 ? "s" : ""} on the public Projects page
        </p>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-project-btn">
          <Plus size={15} /> Add Project
        </button>
      </div>

      {showForm && (
        <ProjectForm
          onClose={() => setShowForm(false)}
          onCreated={(p) => {
            setItems((arr) => [p, ...arr]);
            onCountChange?.(items.length + 1);
            setShowForm(false);
          }}
        />
      )}

      {loading ? (
        <div className="bg-[var(--m-bg)] border border-[var(--m-border)] p-20 text-center text-[var(--m-ink-3)]">
          Loading...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[var(--m-bg)] border border-[var(--m-border)] p-20 text-center text-[var(--m-ink-3)]">
          No projects added yet. Click <strong>Add Project</strong> to upload your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p) => <AdminProjectCard key={p.id} p={p} onDelete={onDelete} onStatusChange={onStatusChange} />)}
        </div>
      )}
    </div>
  );
};

const AdminProjectCard = ({ p, onDelete, onStatusChange }) => {
  const meta = STATUS_META[p.status] || STATUS_META.completed;
  return (
  <article className="bg-[var(--m-bg)] border border-[var(--m-border)] flex flex-col" data-testid={`admin-project-${p.id}`}>
    <div className="relative aspect-[4/3] bg-[var(--m-bg-3)]">
      {p.video_url ? (
        <video src={fileUrl(p.video_url)} className="w-full h-full object-cover" muted controls />
      ) : p.image_url ? (
        <img src={fileUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[var(--m-ink-3)]">
          <ImageIcon size={36} strokeWidth={1.2} />
        </div>
      )}
      <span className="absolute top-3 left-3 tag tag-gold">{p.category}</span>
      <span
        className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.16em] uppercase font-semibold border bg-[var(--m-bg)]"
        style={{ color: meta.color, borderColor: meta.color }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
        {meta.label}
      </span>
      {p.video_url && <span className="absolute bottom-3 right-3 tag tag-teal"><Video size={11} /> Video</span>}
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <h3 className="font-display text-xl leading-tight">{p.title}</h3>
      {(p.location || p.year) && (
        <div className="mt-1 text-xs uppercase tracking-[0.16em] text-[var(--m-ink-3)] flex items-center gap-2 flex-wrap">
          {p.location && <span className="flex items-center gap-1"><MapPin size={11} /> {p.location}</span>}
          {p.year && <span>• {p.year}</span>}
        </div>
      )}
      {p.spec && <p className="mt-2 text-sm text-[var(--m-ink-2)]">{p.spec}</p>}
      <div className="mt-4 pt-4 border-t border-[var(--m-border)]">
        <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--m-ink-3)] block mb-2">Change Status</label>
        <select
          value={p.status || "completed"}
          onChange={(e) => onStatusChange(p.id, e.target.value)}
          className="w-full border border-[var(--m-border)] bg-[var(--m-bg)] px-3 py-2 text-xs"
          data-testid={`admin-project-status-${p.id}`}
        >
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label.toUpperCase()}</option>)}
        </select>
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--m-border)] flex justify-between items-center text-xs text-[var(--m-ink-3)]">
        <span>{new Date(p.created_at).toLocaleDateString()}</span>
        <button onClick={() => onDelete(p.id)} className="p-2 text-[var(--m-error)] hover:bg-[var(--m-error)] hover:text-white" data-testid={`admin-project-delete-${p.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  </article>
  );
};

const ProjectForm = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ title: "", category: "Residential", location: "", spec: "", description: "" });
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const imgInput = useRef(null);
  const vidInput = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("Image must be under 12 MB"); return; }
    setImageFile(f);
    const r = new FileReader();
    r.onload = () => setImagePreview(r.result);
    r.readAsDataURL(f);
  };

  const onPickVideo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 80 * 1024 * 1024) { toast.error("Video must be under 80 MB"); return; }
    setVideoFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category) {
      toast.error("Title and category are required");
      return;
    }
    if (!imageFile && !videoFile) {
      toast.error("Upload at least one image or video");
      return;
    }
    setSubmitting(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("location", form.location);
      fd.append("spec", form.spec);
      fd.append("description", form.description);
      fd.append("status", form.status);
      fd.append("year", form.year);
      if (imageFile) fd.append("image", imageFile);
      if (videoFile) fd.append("video", videoFile);
      const project = await createProject(fd, setProgress);
      toast.success("Project added to public site");
      onCreated(project);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="project-form-modal">
      <div className="bg-[var(--m-bg)] border border-[var(--m-border)] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[var(--m-border)] sticky top-0 bg-[var(--m-bg)]">
          <div>
            <span className="eyebrow">New Project</span>
            <h3 className="font-display text-2xl mt-2">Add to public gallery</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--m-bg-2)]" data-testid="project-form-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6" data-testid="project-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div className="md:col-span-2">
              <label className="label-tiny">Project Title *</label>
              <input className="input-line" required value={form.title} onChange={set("title")} placeholder="e.g. Skyline Heights — S+10 Apartment" data-testid="project-title" />
            </div>
            <div>
              <label className="label-tiny">Category *</label>
              <select className="input-line bg-[var(--m-bg)]" value={form.category} onChange={set("category")} data-testid="project-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny">Status *</label>
              <select className="input-line bg-[var(--m-bg)]" value={form.status} onChange={set("status")} data-testid="project-status">
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny">Location</label>
              <input className="input-line" value={form.location} onChange={set("location")} placeholder="North Bengaluru" data-testid="project-location" />
            </div>
            <div>
              <label className="label-tiny">Year / Timeline</label>
              <input className="input-line" value={form.year} onChange={set("year")} placeholder="e.g. 2024 or 2024–2025" data-testid="project-year" />
            </div>
            <div className="md:col-span-2">
              <label className="label-tiny">Specs / Headline</label>
              <input className="input-line" value={form.spec} onChange={set("spec")} placeholder="S+10 • MIVAN Tech • 84 Units" data-testid="project-spec" />
            </div>
            <div className="md:col-span-2">
              <label className="label-tiny">Description (optional)</label>
              <textarea className="input-line resize-none" rows={2} value={form.description} onChange={set("description")} placeholder="Notes about the project..." data-testid="project-description" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FileSlot
              label="Cover Image"
              hint="JPG / PNG / WEBP up to 12 MB"
              accept="image/jpeg,image/png,image/webp,image/gif"
              file={imageFile}
              preview={imagePreview}
              onPick={onPickImage}
              onClear={() => { setImageFile(null); setImagePreview(""); imgInput.current && (imgInput.current.value = ""); }}
              icon={ImageIcon}
              inputRef={imgInput}
              testId="project-image-input"
            />
            <FileSlot
              label="Project Video"
              hint="MP4 / MOV / WEBM up to 80 MB"
              accept="video/mp4,video/quicktime,video/webm"
              file={videoFile}
              onPick={onPickVideo}
              onClear={() => { setVideoFile(null); vidInput.current && (vidInput.current.value = ""); }}
              icon={Video}
              inputRef={vidInput}
              testId="project-video-input"
            />
          </div>

          {submitting && progress > 0 && (
            <div className="bg-[var(--m-bg-2)] h-1.5 overflow-hidden" data-testid="project-progress">
              <div className="bg-[var(--m-teal)] h-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="project-form-submit">
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {submitting ? `Uploading... ${progress}%` : "Publish Project"}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FileSlot = ({ label, hint, accept, file, preview, onPick, onClear, icon: Icon, inputRef, testId }) => (
  <div>
    <label className="label-tiny">{label}</label>
    <div
      className="border-2 border-dashed border-[var(--m-border)] hover:border-[var(--m-teal)] transition-colors p-4 cursor-pointer relative"
      onClick={() => !file && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onPick}
        className="hidden"
        data-testid={testId}
      />
      {file ? (
        <div className="flex items-center gap-3">
          {preview ? (
            <img src={preview} alt="preview" className="w-16 h-16 object-cover" />
          ) : (
            <div className="w-16 h-16 bg-[var(--m-bg-2)] flex items-center justify-center">
              <Icon size={22} strokeWidth={1.4} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{file.name}</div>
            <div className="text-xs text-[var(--m-ink-3)]">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
          </div>
          <button type="button" onClick={(e) => { e.stopPropagation(); onClear(); }} className="p-2 hover:bg-[var(--m-error)] hover:text-white">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Icon size={28} className="text-[var(--m-ink-3)] mb-2" strokeWidth={1.3} />
          <div className="text-sm">Click to upload</div>
          <div className="text-xs text-[var(--m-ink-3)] mt-1">{hint}</div>
        </div>
      )}
    </div>
  </div>
);
